import React, { useEffect, useState } from 'react';
import { adminViewForm, getPreSignedUrl, verifyForm, rejectForm, createCard, updateFormData } from '../../../../../Actions/FormActions';

const LabelValue = ({ label, value }) => (
  <div className="mb-3">
    <div className="text-sm text-black">{label}</div>
    <div className="text-lg font-extrabold text-black break-words">{value || '-'}</div>
  </div>
);

const EditableField = ({
  label,
  value,
  onChange,
  isEditing,
  type = 'text',
  options
}) => {
  return (
    <div className="mb-3">
      <div className="text-sm text-black">{label}</div>
      {isEditing ? (
        type === 'textarea' ? (
          <textarea
            className="mt-1 w-full border border-gray-300 rounded px-2 py-1 text-sm"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
          />
        ) : type === 'select' ? (
          <select
            className="mt-1 w-full border border-gray-300 rounded px-2 py-1 text-sm"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
          >
            {(options || []).map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        ) : type === 'checkbox' ? (
          <input
            type="checkbox"
            className="mt-1 h-4 w-4"
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
          />
        ) : (
          <input
            type={type}
            className="mt-1 w-full border border-gray-300 rounded px-2 py-1 text-sm"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
          />
        )
      ) : (
        <div className="text-lg font-extrabold text-black break-words">{value || '-'}</div>
      )}
    </div>
  );
};

const ViewFormDialog = ({ isOpen, onClose, formId, onAfterAction }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [medicalCertificateFile, setMedicalCertificateFile] = useState(null);
  const [disabilityCertificateFile, setDisabilityCertificateFile] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!isOpen || !formId) return;
      setLoading(true);
      setError('');
      try {
        const res = await adminViewForm(formId);
        if (!cancelled) {
          if (res?.success === true && res?.data) {
            setData(res.data);
            // Deep clone to keep a separate editable copy
            try {
              setEditData(JSON.parse(JSON.stringify(res.data)));
            } catch (_) {
              setEditData(res.data);
            }
          } else {
            throw new Error(res?.message || 'Failed to load form');
          }
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Something went wrong');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [isOpen, formId]);

  useEffect(() => {
    if (data && !isEditing) {
      try {
        setEditData(JSON.parse(JSON.stringify(data)));
      } catch (_) {
        setEditData(data);
      }
    }
  }, [data, isEditing]);

  if (!isOpen) return null;

  const cmToFeetInches = (cm) => {
    try {
      const totalInches = cm / 2.54;
      const feet = Math.floor(totalInches / 12);
      const remainingInches = Math.round(totalInches % 12);
      return `${feet}ft ${remainingInches}"`;
    } catch (_) {
      return '';
    }
  };

  const formatHeight = (cmValue) => {
    const cmNum = Number(cmValue);
    if (Number.isFinite(cmNum) && cmNum > 0) {
      return `${cmToFeetInches(cmNum)} / ${cmNum} cm`;
    }
    return cmValue ? `${cmValue} cm` : '-';
  };

  const formatYesNo = (value) => {
    try {
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';
      if (typeof value === 'string') {
        const v = value.toLowerCase();
        if (v === 'yes') return 'Yes';
        if (v === 'no') return 'No';
      }
      if (value == null || value === '') return '-';
      return String(value);
    } catch (_) {
      return '-';
    }
  };

  const formatSymbols = (arr) => {
    try {
      if (Array.isArray(arr) && arr.length > 0) return arr.join(', ');
      return '-';
    } catch (_) {
      return '-';
    }
  };

  const openCertificateIfAvailable = async (cert) => {
    try {
      if (cert && cert.presigned_url) {
        window.open(cert.presigned_url, '_blank');
        return;
      }
      if (cert && cert.content_base64 && cert.content_type) {
        const dataUrl = `data:${cert.content_type};base64,${cert.content_base64}`;
        window.open(dataUrl, '_blank');
        return;
      }
      if (cert && cert.file_key) {
        const resp = await getPreSignedUrl(cert.file_key);
        if (resp?.success && resp?.data?.presigned_url) {
          window.open(resp.data.presigned_url, '_blank');
          return;
        }
      }
      if (typeof cert === 'string' && cert.startsWith('http')) {
        window.open(cert, '_blank');
        return;
      }
      alert('Certificate not available');
    } catch (_) {
      alert('Failed to open certificate');
    }
  };

  const handleSaveChanges = async () => {
    if (!editData || !formId) return;
    try {
      setSaving(true);

      // Prepare clean payload without File objects for certificates
      const payload = {
        ...editData,
        medical_condition: {
          ...(editData.medical_condition || {}),
          certificate: (editData.medical_condition || {}).certificate
        },
        disability: {
          ...(editData.disability || {}),
          certificate: (editData.disability || {}).certificate
        }
      };

      const medicalFileToSend = medicalCertificateFile;
      const disabilityFileToSend = disabilityCertificateFile;

      const res = await updateFormData(
        formId,
        payload,
        medicalFileToSend,
        disabilityFileToSend
      );

      if (res?.data) {
        setData(res.data);
        try {
          setEditData(JSON.parse(JSON.stringify(res.data)));
        } catch (_) {
          setEditData(res.data);
        }
        setIsEditing(false);
        setMedicalCertificateFile(null);
        setDisabilityCertificateFile(null);
        setError('');
        if (typeof onAfterAction === 'function') onAfterAction();
      } else {
        throw new Error(res?.message || 'Failed to update form');
      }
    } catch (e) {
      setError(e?.message || 'Failed to update form');
    } finally {
      setSaving(false);
    }
  };

  const current = isEditing && editData ? editData : data || {};
  const isSavedStatus = current?.status?.toLowerCase?.() === 'saved';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-[90%] max-h-[90vh] overflow-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">View Form Details</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {loading && (
          <div className="p-4 text-center text-gray-600">Loading...</div>
        )}
        {error && !loading && (
          <div className="p-4 mb-4 bg-red-100 border border-red-300 text-red-700 rounded">{error}</div>
        )}
        {!loading && !error && data && (
          <div className="space-y-4">

            <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
              <div className="md:col-span-2 space-y-4">
                <div className="border rounded-lg p-4 bg-[#F3EDF9]">
                  <div className="text-sm font-extrabold mb-2 text-[#118B91]">Patient Details</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <EditableField
                      label="Name"
                      value={current?.full_name}
                      isEditing={isEditing}
                      onChange={(val) => setEditData(prev => ({ ...prev, full_name: val }))}
                    />
                    <EditableField
                      label="Age"
                      type="number"
                      value={current?.age}
                      isEditing={isEditing}
                      onChange={(val) => setEditData(prev => ({ ...prev, age: val }))}
                    />
                    <EditableField
                      label="Gender"
                      type={isEditing ? 'select' : 'text'}
                      value={current?.gender}
                      isEditing={isEditing}
                      options={[
                        { value: '', label: 'Select' },
                        { value: 'Male', label: 'Male' },
                        { value: 'Female', label: 'Female' },
                        { value: 'Other', label: 'Other' }
                      ]}
                      onChange={(val) => setEditData(prev => ({ ...prev, gender: val }))}
                    />
                    <EditableField
                      label="Height"
                      type="number"
                      value={isEditing ? current?.height : (current?.height ? formatHeight(current.height) : '-')}
                      isEditing={isEditing}
                      onChange={(val) => setEditData(prev => ({ ...prev, height: val }))}
                    />
                    <EditableField
                      label="Weight"
                      type="number"
                      value={current?.weight}
                      isEditing={isEditing}
                      onChange={(val) => setEditData(prev => ({ ...prev, weight: val }))}
                    />
                    <EditableField
                      label="Blood group"
                      value={current?.blood_group}
                      isEditing={isEditing}
                      onChange={(val) => setEditData(prev => ({ ...prev, blood_group: val }))}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <EditableField
                      label="Medical Condition Name"
                      value={current?.medical_condition?.name}
                      isEditing={isEditing}
                      onChange={(val) =>
                        setEditData(prev => ({
                          ...prev,
                          medical_condition: { ...(prev.medical_condition || {}), name: val }
                        }))
                      }
                    />
                    <EditableField
                      label="Doctor's Name"
                      value={current?.medical_condition?.doctor_name}
                      isEditing={isEditing}
                      onChange={(val) =>
                        setEditData(prev => ({
                          ...prev,
                          medical_condition: { ...(prev.medical_condition || {}), doctor_name: val }
                        }))
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Medical Condition Card */}
            <div className='grid grid-cols-1 md:grid-cols-1 gap-4'>
              <div className='space-y-4'>
                <div className="border rounded-lg p-4 bg-[#F3EDF9]">
                  <div className="text-sm font-extrabold mb-2 text-[#118B91]">Medical Details</div>
                  <EditableField
                    label="Medical Notes"
                    type="textarea"
                    value={current?.medical_condition?.notes}
                    isEditing={isEditing}
                    onChange={(val) =>
                      setEditData(prev => ({
                        ...prev,
                        medical_condition: { ...(prev.medical_condition || {}), notes: val }
                      }))
                    }
                  />
                  <EditableField
                    label="Allergies"
                    type="textarea"
                    value={current?.medical_condition?.allergies}
                    isEditing={isEditing}
                    onChange={(val) =>
                      setEditData(prev => ({
                        ...prev,
                        medical_condition: { ...(prev.medical_condition || {}), allergies: val }
                      }))
                    }
                  />
                  {(() => {
                    const med = current?.medical_condition?.certificate;
                  const hasAny = (med && (med.presigned_url || med.content_base64));
                    const hasExisting = hasAny;

                    const renderCert = (label, cert) => {
                    if (!cert || (!cert.presigned_url && !cert.content_base64)) return null;
                    const type = cert.content_type || '';
                    const isImage = typeof type === 'string' && type.startsWith('image/');
                    const isPdf = typeof type === 'string' && type.toLowerCase().includes('pdf');
                    const src = cert.presigned_url || `data:${type || 'application/octet-stream'};base64,${cert.content_base64}`;
                      return (
                        <div className="space-y-2">
                          <div className="text-sm text-black">{label}</div>
                          {isImage ? (
                            <img
                            src={src}
                              alt={label}
                              className="w-full max-h-[360px] object-contain rounded-lg border"
                            />
                          ) : isPdf ? (
                          <object data={src} type={type || 'application/pdf'} className="w-full h-[480px] rounded-lg border">
                            <a href={src} target="_blank" rel="noreferrer" className="text-blue-600 underline">Open {label}</a>
                            </object>
                          ) : (
                          <a href={src} target="_blank" rel="noreferrer" className="inline-block px-4 py-2 bg-[#FFD12B] hover:bg-[#eab73f] text-[#753ABF] font-bold rounded">Open {label}</a>
                          )}
                        </div>
                      );
                    };

                    return (
                      <div className="mt-2 grid grid-cols-1 rounded-lg gap-4 bg-[#F3EDF9]">
                        {hasExisting && renderCert('Medical Certificate', med)}
                        {isEditing && (
                          <div className="space-y-2">
                            <div className="text-sm text-black font-medium">Upload new Medical Certificate</div>
                            <input
                              type="file"
                              accept="image/*,application/pdf"
                              onChange={(e) => {
                                const file = e.target.files && e.target.files[0];
                                setMedicalCertificateFile(file || null);
                              }}
                              className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-[#E0D2EE] file:text-[#753ABF] hover:file:bg-[#D2C1EA]"
                            />
                            {medicalCertificateFile && (
                              <div className="text-xs text-gray-600 break-all">
                                Selected: {medicalCertificateFile.name}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Additional details */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2 space-y-4">
                <div className="border rounded-lg p-4 bg-[#F3EDF9]">
                  <div className="text-sm font-extrabold mb-2 text-[#118B91]">Address</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <EditableField
                      label="Line 1"
                      value={current?.address?.line_1}
                      isEditing={isEditing}
                      onChange={(val) =>
                        setEditData(prev => ({
                          ...prev,
                          address: { ...(prev.address || {}), line_1: val }
                        }))
                      }
                    />
                    <EditableField
                      label="Line 2"
                      value={current?.address?.line_2}
                      isEditing={isEditing}
                      onChange={(val) =>
                        setEditData(prev => ({
                          ...prev,
                          address: { ...(prev.address || {}), line_2: val }
                        }))
                      }
                    />
                    <EditableField
                      label="City"
                      value={current?.address?.city}
                      isEditing={isEditing}
                      onChange={(val) =>
                        setEditData(prev => ({
                          ...prev,
                          address: { ...(prev.address || {}), city: val }
                        }))
                      }
                    />
                    <EditableField
                      label="District"
                      value={current?.address?.district}
                      isEditing={isEditing}
                      onChange={(val) =>
                        setEditData(prev => ({
                          ...prev,
                          address: { ...(prev.address || {}), district: val }
                        }))
                      }
                    />
                    <EditableField
                      label="State"
                      value={current?.address?.state}
                      isEditing={isEditing}
                      onChange={(val) =>
                        setEditData(prev => ({
                          ...prev,
                          address: { ...(prev.address || {}), state: val }
                        }))
                      }
                    />
                    <EditableField
                      label="Pincode"
                      value={current?.address?.pincode}
                      isEditing={isEditing}
                      onChange={(val) =>
                        setEditData(prev => ({
                          ...prev,
                          address: { ...(prev.address || {}), pincode: val }
                        }))
                      }
                    />
                  </div>
                </div>

                <div className="border rounded-lg p-4 bg-[#F3EDF9]">
                  <div className="text-sm font-extrabold mb-2 text-[#118B91]">Emergency Contacts</div>
                  {Array.isArray(current?.emergency_contacts) && current.emergency_contacts.length > 0 ? (
                    <div className="space-y-3">
                      {current.emergency_contacts.map((c, idx) => (
                        <div key={idx} className="p-3">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <EditableField
                              label="Name"
                              value={c?.name}
                              isEditing={isEditing}
                              onChange={(val) =>
                                setEditData(prev => {
                                  const list = Array.isArray(prev.emergency_contacts) ? [...prev.emergency_contacts] : [];
                                  list[idx] = { ...(list[idx] || {}), name: val };
                                  return { ...prev, emergency_contacts: list };
                                })
                              }
                            />
                            <EditableField
                              label="Relationship"
                              value={c?.relationship}
                              isEditing={isEditing}
                              onChange={(val) =>
                                setEditData(prev => {
                                  const list = Array.isArray(prev.emergency_contacts) ? [...prev.emergency_contacts] : [];
                                  list[idx] = { ...(list[idx] || {}), relationship: val };
                                  return { ...prev, emergency_contacts: list };
                                })
                              }
                            />
                            <EditableField
                              label="Mobile"
                              value={c?.mobile}
                              isEditing={isEditing}
                              onChange={(val) =>
                                setEditData(prev => {
                                  const list = Array.isArray(prev.emergency_contacts) ? [...prev.emergency_contacts] : [];
                                  list[idx] = { ...(list[idx] || {}), mobile: val };
                                  return { ...prev, emergency_contacts: list };
                                })
                              }
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <LabelValue label="Contacts" value="-" />
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <div className="border rounded-lg p-4 bg-[#F3EDF9]">
                  <div className="text-sm font-extrabold mb-2 text-[#118B91]">Contact & Preferences</div>
                  <div className="grid grid-cols-1 gap-y-3">
                    <EditableField
                      label="Mobile"
                      value={current?.mobile}
                      isEditing={isEditing}
                      onChange={(val) => setEditData(prev => ({ ...prev, mobile: val }))}
                    />
                    <EditableField
                      label="Email"
                      value={current?.email}
                      isEditing={isEditing}
                      onChange={(val) => setEditData(prev => ({ ...prev, email: val }))}
                    />
                    <EditableField
                      label="Consent given"
                      type="checkbox"
                      value={current?.consent}
                      isEditing={isEditing}
                      onChange={(val) => setEditData(prev => ({ ...prev, consent: val }))}
                    />
                    <EditableField
                      label="Terms accepted"
                      type="checkbox"
                      value={current?.terms_and_conditions}
                      isEditing={isEditing}
                      onChange={(val) => setEditData(prev => ({ ...prev, terms_and_conditions: val }))}
                    />
                    <EditableField
                      label="Lost & Found enabled"
                      type="checkbox"
                      value={typeof current?.lost_and_found === 'boolean' ? current.lost_and_found : current?.lost_n_found}
                      isEditing={isEditing}
                      onChange={(val) => setEditData(prev => ({ ...prev, lost_and_found: val }))}
                    />
                    <EditableField
                      label="Symbols (comma separated)"
                      value={Array.isArray(current?.symbols) ? current.symbols.join(', ') : ''}
                      isEditing={isEditing}
                      onChange={(val) =>
                        setEditData(prev => ({
                          ...prev,
                          symbols: val
                            .split(',')
                            .map(s => s.trim())
                            .filter(Boolean)
                        }))
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="border rounded-lg p-4 bg-[#F3EDF9]">
              <div className="text-sm font-extrabold mb-2 text-[#118B91]">Disability</div>
              <div className="grid grid-cols-1 gap-y-3">
                <EditableField
                  label="Has disability"
                  type="checkbox"
                  value={current?.disability?.has_disability}
                  isEditing={isEditing}
                  onChange={(val) =>
                    setEditData(prev => ({
                      ...prev,
                      disability: { ...(prev.disability || {}), has_disability: val }
                    }))
                  }
                />
                <EditableField
                  label="Type"
                  value={current?.disability?.type}
                  isEditing={isEditing}
                  onChange={(val) =>
                    setEditData(prev => ({
                      ...prev,
                      disability: { ...(prev.disability || {}), type: val }
                    }))
                  }
                />
                {(() => {
                  const dis = current?.disability?.certificate;
                  const hasAny = (dis && (dis.presigned_url || dis.content_base64));
                  const hasExisting = hasAny;

                  const renderCert = (label, cert) => {
                    if (!cert || (!cert.presigned_url && !cert.content_base64)) return null;
                    const type = cert.content_type || '';
                    const isImage = typeof type === 'string' && type.startsWith('image/');
                    const isPdf = typeof type === 'string' && type.toLowerCase().includes('pdf');
                    const src = cert.presigned_url || `data:${type || 'application/octet-stream'};base64,${cert.content_base64}`;
                    return (
                      <div className="space-y-2">
                        <div className="text-sm text-black font-extrabold text-[#118B91]">{label}</div>
                        {isImage ? (
                          <img
                            src={src}
                            alt={label}
                            className="w-full max-h-[360px] object-contain rounded-lg border"
                          />
                        ) : isPdf ? (
                          <object data={src} type={type || 'application/pdf'} className="w-full h-[480px] rounded-lg border">
                            <a href={src} target="_blank" rel="noreferrer" className="text-blue-600 underline">Open {label}</a>
                          </object>
                        ) : (
                          <a href={src} target="_blank" rel="noreferrer" className="inline-block px-4 py-2 bg-[#FFD12B] hover:bg-[#eab73f] text-[#753ABF] font-bold rounded">Open {label}</a>
                        )}
                      </div>
                    );
                  };

                  return (
                    <div className="mt-2 grid grid-cols-1 rounded-lg gap-4 bg-[#F3EDF9]">
                      {hasExisting && renderCert('Disability Certificate', dis)}
                      {isEditing && (
                        <div className="space-y-2">
                          <div className="text-sm text-black font-medium">Upload new Disability Certificate</div>
                          <input
                            type="file"
                            accept="image/*,application/pdf"
                            onChange={(e) => {
                              const file = e.target.files && e.target.files[0];
                              setDisabilityCertificateFile(file || null);
                            }}
                            className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-[#E0D2EE] file:text-[#753ABF] hover:file:bg-[#D2C1EA]"
                          />
                          {disabilityCertificateFile && (
                            <div className="text-xs text-gray-600 break-all">
                              Selected: {disabilityCertificateFile.name}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Payment & Meta */}
            <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
              <div className="border rounded-lg p-4 bg-[#F3EDF9]">
                <div className="text-sm font-extrabold mb-2 text-[#118B91]">Payment Info</div>
                <div className="grid grid-cols-1 gap-y-3">
                  <LabelValue label="Status" value={current?.status} />
                  <LabelValue label="Order ID" value={current?.payment?.razorpay_order_id} />
                  <LabelValue label="Payment ID" value={current?.payment?.razorpay_payment_id} />
                  <LabelValue label="Signature" value={current?.payment?.razorpay_signature} />
                </div>
              </div>
            </div>


          </div>
        )}

        <div className="mt-6 flex justify-between items-center gap-2">
          <div className="flex items-center gap-2">
            {!isSavedStatus && (
              <>
                {!isEditing ? (
                  <button
                    type="button"
                    disabled={actionLoading}
                    onClick={() => setIsEditing(true)}
                    className={`px-4 py-2 rounded bg-purple-100 text-[#753ABF] hover:bg-purple-200 ${actionLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
                  >
                    Edit
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => {
                        // Reset edits to last loaded data
                        setIsEditing(false);
                        setMedicalCertificateFile(null);
                        setDisabilityCertificateFile(null);
                        try {
                          setEditData(JSON.parse(JSON.stringify(data)));
                        } catch (_) {
                          setEditData(data);
                        }
                      }}
                      className={`px-4 py-2 rounded bg-gray-100 text-gray-700 hover:bg-gray-200 ${saving ? 'opacity-60 cursor-not-allowed' : ''}`}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={saving}
                      onClick={handleSaveChanges}
                      className={`px-4 py-2 rounded bg-[#753ABF] text-white hover:bg-[#5f2f99] ${saving ? 'opacity-60 cursor-not-allowed' : ''}`}
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                  </>
                )}
              </>
            )}
          </div>

          <div className="flex justify-end gap-2">
          {current?.status?.toLowerCase?.() === 'submitted' && (
            <>
              <button
                type="button"
                disabled={actionLoading}
                onClick={async () => {
                  try {
                    setActionLoading(true);
                    await verifyForm(formId);
                    if (typeof onAfterAction === 'function') onAfterAction();
                    onClose();
                  } catch (e) {
                    setError(e?.message || 'Failed to verify form');
                  } finally {
                    setActionLoading(false);
                  }
                }}
                className={`px-4 py-2 rounded ${actionLoading ? 'opacity-60 cursor-not-allowed' : ''} bg-green-100 text-green-700 hover:bg-green-200`}
              >
                Verify
              </button>
              <button
                type="button"
                disabled={actionLoading}
                onClick={async () => {
                  try {
                    setActionLoading(true);
                    await rejectForm(formId);
                    if (typeof onAfterAction === 'function') onAfterAction();
                    onClose();
                  } catch (e) {
                    setError(e?.message || 'Failed to reject form');
                  } finally {
                    setActionLoading(false);
                  }
                }}
                className={`px-4 py-2 rounded ${actionLoading ? 'opacity-60 cursor-not-allowed' : ''} bg-red-100 text-red-700 hover:bg-red-200`}
              >
                Reject
              </button>
            </>
          )}
          {data?.status?.toLowerCase?.() === 'verified' && (
            <button
              type="button"
              disabled={actionLoading}
              onClick={async () => {
                try {
                  setActionLoading(true);
                  const res = await createCard(data?.user_id || data?.user?._id, formId);
                  const message = res?.message || 'Card created successfully';
                  alert(message);
                  if (typeof onAfterAction === 'function') onAfterAction();
                  onClose();
                } catch (e) {
                  setError(e?.message || 'Failed to create card');
                } finally {
                  setActionLoading(false);
                }
              }}
              className={`px-4 py-2 rounded ${actionLoading ? 'opacity-60 cursor-not-allowed' : ''} bg-blue-100 text-blue-700 hover:bg-blue-200`}
            >
              Create Card
            </button>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewFormDialog;
